import "./site/error-beacon";
import { Foraging, Pickup } from "./games/foraging/foraging";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.foraging_pb";
const HANDLE_KEY = "aether.handle";

// Retry any submits that failed on a prior page load (transient 5xx / network).
flushFailedSubmits("foraging");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startForagingRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/foraging", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishForagingRun(runId: string, handle: string, pickups: Pickup[]) {
  const url = `/api/runs/foraging/${runId}`;
  const body = { handle, pickups };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("foraging", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("foraging", url, body);
    return null;
  }
}

/** v5(b): render the run's brew-arc as a one-line characterization
 *  sourced from the server response. brews_by_color crosses the wire
 *  on every submit; the §6 #2 "chased value, not chains" candidate is
 *  surfaced when the player picked many orbs but never composed a brew.
 *  See dossier 05-foraging/07-telemetry.md §6 #2. */
function brewArcTag(
  brews: number,
  brews_by_color: { cyan: number; magenta: number; gold: number },
  pickups: number,
): string {
  if (pickups < 10) return "";
  if (brews === 0) {
    return pickups >= 30 ? "chased value · 0 brews this run" : "";
  }
  const parts: string[] = [];
  if (brews_by_color.cyan > 0) parts.push(`${brews_by_color.cyan}C`);
  if (brews_by_color.magenta > 0) parts.push(`${brews_by_color.magenta}M`);
  if (brews_by_color.gold > 0) parts.push(`${brews_by_color.gold}G`);
  const split = parts.length > 0 ? ` · ${parts.join(" ")}` : "";
  if (brews >= 10) return `composed deep${split}`;
  if (brews >= 5) return `chain-builder${split}`;
  return `composer${split}`;
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudPickups = document.getElementById("hud-pickups")!;
const hudTime = document.getElementById("hud-time")!;
const hudScore = document.getElementById("hud-score")!;
const hudBrews = document.getElementById("hud-brews");
const magnetChip = document.getElementById("magnet-chip") as HTMLElement | null;
const magnetTimer = document.getElementById("magnet-timer") as HTMLElement | null;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Foraging | null = null;
let activeRunId: string | null = null;
let pendingPickups: Pickup[] | null = null;

async function startRun() {
  if (game) game.dispose();
  const dseed = getDailySeed();
  const runStart = dseed === null ? await startForagingRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Foraging(canvas, {
    onHud: (state) => {
      hudPickups.textContent = String(state.pickups);
      hudTime.textContent = ((90_000 - state.runMs) / 1000).toFixed(1);
      hudScore.textContent = state.scoreEstimate.toLocaleString();
      if (hudBrews) hudBrews.textContent = String(state.brews);
      if (magnetChip) {
        const active = state.magnetTimeLeftMs > 0;
        magnetChip.classList.toggle("visible", active);
        if (magnetTimer) magnetTimer.textContent = active ? `${(state.magnetTimeLeftMs / 1000).toFixed(1)}s` : "";
      }
    },
    onGameOver: async ({ pickups, scoreEstimate, bestChain, pickupCount, brews }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = "Run complete";
      const brewsTag = brews > 0 ? ` · ${brews} brews` : "";
      msgBody.textContent = `${scoreEstimate.toLocaleString()} pts · ${pickupCount} orbs · best chain ${bestChain}${brewsTag}`;
      btnStart.textContent = "Try again";
      if (magnetChip) magnetChip.classList.remove("visible");
      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (scoreEstimate > pb) localStorage.setItem(PB_KEY, String(scoreEstimate));
      } catch { /* private */ }
      const xp = recordRun({ game: "foraging", score: scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("foraging", scoreEstimate);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishForagingRun(activeRunId, handle, pickups);
        if (res) {
          msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
          const tag = brewArcTag(res.brews ?? brews, res.brews_by_color ?? { cyan: 0, magenta: 0, gold: 0 }, res.pickups ?? pickupCount);
          if (tag) msgBody.textContent += ` · ${tag}`;
        }
      } else {
        pendingPickups = pickups;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Foraging";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingPickups) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishForagingRun(activeRunId, value, pendingPickups);
  pendingPickups = null;
  submitForm.hidden = true;
  if (res) {
    msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
    const tag = brewArcTag(res.brews ?? 0, res.brews_by_color ?? { cyan: 0, magenta: 0, gold: 0 }, res.pickups ?? 0);
    if (tag) msgBody.textContent += ` · ${tag}`;
  }
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
